/**
 * API Client para o módulo de Agents
 *
 * Todas as chamadas usam o token JWT do Supabase para autenticação.
 */

import { SUPABASE_URL } from "@/integrations/supabase/client";
import { apiFetch } from "@/lib/api-fetch";
import type {
  ChatRequest,
  ChatResponse,
  ChatSessionSummary,
  ChatSessionDetail,
  PortfolioAnalysisResponse,
  BatchPortfolioRequest,
  BatchPortfolioResponse,
  EnhancedMatchResponse,
  MatchExplanationResponse,
  ETLSyncRequest,
  ETLSyncResponse,
  ETLStatusResponse,
  ETLRunSummary,
  AgentStatsResponse,
  VagaAssistantSessionSummary,
  VagaAssistantSessionMessages,
  MessageCardsPage,
} from "@/types/agents";

const AGENTS_BASE_URL = `${SUPABASE_URL}agents`;

function fetchWithAuth<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  return apiFetch<T>(`${AGENTS_BASE_URL}${endpoint}`, options);
}

// ============================================
// Chat API
// ============================================

/**
 * Envia mensagem para o chat
 */
export async function sendChatMessage(request: ChatRequest): Promise<ChatResponse> {
  return fetchWithAuth<ChatResponse>("/chat", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

/**
 * Lista sessões de chat do usuário
 */
export async function getChatSessions(limit: number = 20): Promise<ChatSessionSummary[]> {
  return fetchWithAuth<ChatSessionSummary[]>(`/chat/sessions?limit=${limit}`);
}

/**
 * Obtém detalhes de uma sessão de chat
 */
export async function getChatSession(sessionId: string): Promise<ChatSessionDetail> {
  return fetchWithAuth<ChatSessionDetail>(`/chat/sessions/${sessionId}`);
}

// ============================================
// Portfolio API
// ============================================

/**
 * Analisa portfolio de um freelancer
 */
export async function analyzePortfolio(
  freelancerId: string,
  forceReanalysis: boolean = false
): Promise<PortfolioAnalysisResponse> {
  const params = forceReanalysis ? "?force_reanalysis=true" : "";
  return fetchWithAuth<PortfolioAnalysisResponse>(`/portfolio/analyze/${freelancerId}${params}`, {
    method: "POST",
  });
}

/**
 * Obtém análise existente de portfolio
 */
export async function getPortfolioAnalysis(
  freelancerId: string
): Promise<PortfolioAnalysisResponse | null> {
  try {
    return await fetchWithAuth<PortfolioAnalysisResponse>(`/portfolio/${freelancerId}`);
  } catch (error) {
    // Retorna null se não encontrar análise
    if (error instanceof Error && error.message.includes("404")) {
      return null;
    }
    throw error;
  }
}

/**
 * Analisa múltiplos portfolios em lote
 */
export async function batchAnalyzePortfolios(
  request: BatchPortfolioRequest
): Promise<BatchPortfolioResponse> {
  return fetchWithAuth<BatchPortfolioResponse>("/portfolio/batch-analyze", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

/**
 * Resumo de análise de portfolio para listagem
 */
export interface PortfolioAnalysisSummary {
  freelancer_id: string;
  overall_score: number;
  analyzed_at: string;
}

/**
 * Lista todos os portfolios já analisados
 */
export async function listPortfolioAnalyses(): Promise<PortfolioAnalysisSummary[]> {
  return fetchWithAuth<PortfolioAnalysisSummary[]>("/portfolio/list");
}

// ============================================
// Match API
// ============================================

export interface GetMatchesParams {
  limit?: number;
  minScore?: number;
  includePortfolio?: boolean;
  includeExplanation?: boolean;
}

/**
 * Obtém matches enriquecidos para uma vaga
 */
export async function getEnhancedMatches(
  vagaId: string,
  params: GetMatchesParams = {}
): Promise<EnhancedMatchResponse> {
  const queryParams = new URLSearchParams();

  if (params.limit !== undefined) {
    queryParams.set("limit", params.limit.toString());
  }
  if (params.minScore !== undefined) {
    queryParams.set("min_score", params.minScore.toString());
  }
  if (params.includePortfolio !== undefined) {
    queryParams.set("include_portfolio", params.includePortfolio.toString());
  }
  if (params.includeExplanation !== undefined) {
    queryParams.set("include_explanation", params.includeExplanation.toString());
  }

  const queryString = queryParams.toString();
  const endpoint = `/match/${vagaId}${queryString ? `?${queryString}` : ""}`;

  return fetchWithAuth<EnhancedMatchResponse>(endpoint);
}

/**
 * Obtém explicação detalhada de um match
 */
export async function getMatchExplanation(
  vagaId: string,
  freelancerId: string
): Promise<MatchExplanationResponse> {
  return fetchWithAuth<MatchExplanationResponse>(`/match/${vagaId}/explain/${freelancerId}`, {
    method: "POST",
  });
}

// ============================================
// ETL API
// ============================================

/**
 * Dispara sincronização ETL
 */
export async function triggerETLSync(request: ETLSyncRequest): Promise<ETLSyncResponse> {
  return fetchWithAuth<ETLSyncResponse>("/etl/sync", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

/**
 * Obtém status atual do ETL
 */
export async function getETLStatus(): Promise<ETLStatusResponse> {
  return fetchWithAuth<ETLStatusResponse>("/etl/status");
}

/**
 * Lista histórico de execuções do ETL
 */
export async function getETLRuns(limit: number = 10): Promise<ETLRunSummary[]> {
  return fetchWithAuth<ETLRunSummary[]>(`/etl/runs?limit=${limit}`);
}

// ============================================
// Stats API
// ============================================

/**
 * Obtém estatísticas do módulo de agents
 */
export async function getAgentStats(): Promise<AgentStatsResponse> {
  return fetchWithAuth<AgentStatsResponse>("/stats");
}

// ============================================
// Vaga Assistant API
// ============================================

/**
 * Lista todas as sessões do assistente do usuário (cross-vaga)
 */
export async function getVagaAssistantSessions(
  limit: number = 30
): Promise<VagaAssistantSessionSummary[]> {
  return fetchWithAuth<VagaAssistantSessionSummary[]>(
    `/vaga-assistant/sessions?limit=${limit}`
  );
}

/**
 * Obtém mensagens de uma sessão do assistente
 */
export async function getVagaAssistantSessionMessages(
  vagaId: string,
  sessionId: string
): Promise<VagaAssistantSessionMessages> {
  return fetchWithAuth<VagaAssistantSessionMessages>(
    `/vaga-assistant/sessions/${vagaId}/${sessionId}/messages`
  );
}

/**
 * Pagina os cards de freelancers persistidos numa mensagem (sob demanda).
 * O backend fatia o JSONB já calculado (o motor de recomendação não roda de novo).
 */
export async function getMessageCardsPage(
  messageId: string,
  page: number,
  pageSize = 10
): Promise<MessageCardsPage> {
  return fetchWithAuth<MessageCardsPage>(
    `/vaga-assistant/messages/${messageId}/cards?page=${page}&page_size=${pageSize}`
  );
}

/**
 * Health check do módulo (não requer autenticação)
 */
export async function checkAgentHealth(): Promise<{ status: string; module: string }> {
  return apiFetch<{ status: string; module: string }>(`${AGENTS_BASE_URL}/health`, { auth: false });
}
